import { ExternalLink, Mail as MailIcon } from "lucide-react";
import { getCounterpartLabel, getCounterpartUrl } from "../lib/counterpart-url";

/** Cross-app navigation element (Memso side).
 *
 *  Renders an "Open Mail" link in the sidebar that takes the user
 *  to the brand-matched WakiOutlookViewer hostname. Brand-aware: on
 *  app.memso.ai it goes to mail.memso.ai; on app.3dbypixel.com it
 *  goes to mail.3dbypixel.com.
 *
 *  When the portable JWT cookie (per design doc 6.5) is in place,
 *  the destination is signed-in automatically - no consent prompt,
 *  no "continue as" button.
 *
 *  Renders null if the current hostname isn't on the known list
 *  (custom unconfigured domain). */
export function CrossAppNav({ collapsed = false }: { collapsed?: boolean }): JSX.Element | null {
  if (typeof window === "undefined") return null;
  const host = window.location.host;
  const resolution = getCounterpartUrl(host);
  if (!resolution) return null;
  const label = getCounterpartLabel(host) ?? "Open Mail";

  if (collapsed) {
    return (
      <a
        href={resolution.counterpartUrl}
        className="group flex h-10 w-10 items-center justify-center rounded-md text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
        title={label}
        aria-label={label}
      >
        <MailIcon className="h-5 w-5" />
        <ExternalLink className="h-3 w-3 opacity-60 -ml-1 -mt-3" aria-hidden />
      </a>
    );
  }

  return (
    <a
      href={resolution.counterpartUrl}
      className="group flex items-center justify-between gap-3 rounded-md border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
      title="Switch to WakiOutlookViewer"
    >
      <span className="flex items-center gap-2">
        <MailIcon className="h-4 w-4" />
        <span className="font-medium">{label}</span>
      </span>
      <ExternalLink className="h-3.5 w-3.5 opacity-60 group-hover:opacity-90" aria-hidden />
    </a>
  );
}
